import React from "react";
import { useNavigate } from "react-router-dom";

// Replace the {{PLACEHOLDERS}} below with your real legal entity, contact email,
// and effective date before submitting to the Play Store. Most placeholders are
// already populated with sensible defaults that you can keep until you have a
// registered company / domain.

const COMPANY = "Quattro Games";
const CONTACT_EMAIL = "support@quattro.app";
const EFFECTIVE_DATE = "February 3, 2026";
const APP_NAME = "Quattro";

export default function Privacy() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen w-full px-4 md:px-8 py-8" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button className="btn-ghost" onClick={() => window.history.length > 1 ? nav(-1) : nav("/")} data-testid="privacy-back-btn">Back</button>
          <h1 className="font-display font-black text-2xl md:text-3xl">Privacy Policy</h1>
        </div>

        <div className="text-white/55 text-xs mb-6">
          Effective: <b>{EFFECTIVE_DATE}</b>
        </div>

        <article className="prose prose-invert space-y-4 text-sm text-white/80">
          <p>
            {COMPANY} ("we", "us") operates the {APP_NAME} mobile and web game
            (the "Service"). This page explains what we collect, why, and how
            you can control it. Plain language, no dark patterns.
          </p>

          <h2 className="font-display font-bold text-lg mt-6 mb-2 text-white">1. What we collect</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li><b>Anonymous game profile</b>: a randomly-generated user ID, your
              chosen display name, virtual chips/gems balances, level/XP, win-loss
              record, daily-streak progress, owned card-back skins, and recent
              match history. Stored on our servers.</li>
            <li><b>Optional Google Sign-In</b>: if you sign in, we receive your
              Google email, name, and profile picture URL. We use these only to
              identify your account across devices. We do NOT post anywhere or
              read other Google data.</li>
            <li><b>Purchases</b>: when you buy gems, we receive a Stripe (web) or
              Google Play Billing (Android) purchase token. We store the
              transaction id, amount, and the gems you received. We never see
              your card number — that goes directly to Stripe / Google.</li>
            <li><b>Ads</b>: We display ads via <b>Google AdSense</b> (web) and
              <b>Google AdMob</b> (mobile). These partners may collect a
              non-personally-identifiable advertising ID (resettable in your
              device / browser settings) and a cookie to show relevant ads and
              measure performance. First-party cookies are used by AdSense on
              our site to serve ads, frequency-cap, and detect invalid traffic.
              You can opt out of personalized ads at
              <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: "#FFD700" }}>google.com/settings/ads</a>
              or in the consent dialog the first time you launch the mobile app.</li>
            <li><b>Diagnostics</b>: server logs (timestamped request paths +
              error stacks) for up to 30 days. No request bodies are logged.</li>
            <li><b>Crash reports</b>: when the app crashes, Sentry receives the
              anonymized stack trace, your device model, OS version, app version,
              and a hashed session id. No personal data, game content, or chat
              messages are included.</li>
          </ul>

          <h2 className="font-display font-bold text-lg mt-6 mb-2 text-white">2. Why we collect it</h2>
          <p>
            To run the multiplayer game (rooms, ante/pot, daily streak, leaderboard
            and tournament rankings), to credit purchases, to show rewarded ads,
            and to keep the service stable. We do not sell your data, ever.
          </p>

          <h2 className="font-display font-bold text-lg mt-6 mb-2 text-white">3. Third parties</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li><b>Google Sign-In</b> — when you choose to sign in with Google.</li>
            <li><b>Stripe</b> (web) and <b>Google Play Billing</b> (Android) — for purchases.</li>
            <li><b>Google AdSense</b> (web) and <b>Google AdMob</b> (mobile) — for display and rewarded ads. Third-party vendors, including Google, use cookies and device identifiers to serve ads based on a user's prior visits. Users may opt out of personalized advertising by visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: "#FFD700" }}>Google Ads Settings</a>.</li>
            <li><b>MongoDB Atlas</b> — encrypted data hosting.</li>
            <li><b>Sentry</b> — anonymized crash reporting.</li>
          </ul>
          <p>Each of the above has their own privacy policy.</p>

          <h2 className="font-display font-bold text-lg mt-6 mb-2 text-white">3a. Cookies &amp; similar technology</h2>
          <p>
            We use a strictly necessary first-party cookie (<code>session_token</code>)
            to keep you signed in after Google OAuth, and a short-lived
            <code> oauth_state</code> cookie during the sign-in round-trip.
            Google AdSense places its own cookies (e.g. <code>IDE</code>,
            <code>__gads</code>) to serve ads, cap frequency, and detect
            invalid traffic — governed by
            <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: "#FFD700" }}>Google's advertising policies</a>.
            If you are in the EEA / UK / Switzerland, AdSense serves only
            non-personalized ads unless you consent to personalization.
          </p>

          <h2 className="font-display font-bold text-lg mt-6 mb-2 text-white">4. Data retention &amp; deletion</h2>
          <p>
            We keep your profile and match history as long as the account is
            active. <b>You can delete your account instantly from Profile →
            Delete my account</b>, which permanently erases your chips, gems,
            match history, purchases, and sign-in. You may also email <a href={`mailto:${CONTACT_EMAIL}`} className="underline" style={{ color: "#FFD700" }}>{CONTACT_EMAIL}</a> from
            the email tied to your Google Sign-In; we respond within 30 days.
            Anonymous (guest) accounts purge automatically after 12 months of
            inactivity.
          </p>

          <h2 className="font-display font-bold text-lg mt-6 mb-2 text-white">5. Children</h2>
          <p>
            {APP_NAME} is rated for ages 13+. We do not knowingly collect data
            from children under 13. If you believe a child has provided data,
            email us and we will delete it.
          </p>

          <h2 className="font-display font-bold text-lg mt-6 mb-2 text-white">6. Your choices</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>Sign out at any time from the lobby header.</li>
            <li>Reset your AdMob advertising ID in Android settings.</li>
            <li>Withdraw ad-personalization consent in-app at any time (Settings → Ad consent).</li>
            <li>Request data export or deletion via email.</li>
          </ul>

          <h2 className="font-display font-bold text-lg mt-6 mb-2 text-white">7. Changes</h2>
          <p>
            If we make material changes we will update the "Effective" date at
            the top and surface a banner in-app on next launch.
          </p>

          <h2 className="font-display font-bold text-lg mt-6 mb-2 text-white">8. Contact</h2>
          <p>
            Questions? <a href={`mailto:${CONTACT_EMAIL}`} className="underline" style={{ color: "#FFD700" }}>{CONTACT_EMAIL}</a>
          </p>
        </article>
      </div>
    </div>
  );
}
